import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Sparkles, Wand2, RotateCcw, Trash2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  {
    key: "chi_siamo_photo",
    label: 'Foto "Chi siamo"',
    desc: "La foto dei titolari nella sezione Chi siamo. Per il risultato migliore usa una foto verticale.",
    def: "/images/chi-siamo.webp",
    imgCls: "w-40 h-52",
  },
];

const DEFAULT_SLIDES = [1, 2, 3, 4, 5, 6].map((n) => `/images/hero-slide-${n}.webp`);
const DEFAULT_STORE = ["/images/interno-negozio.webp"];

const btnPrimary =
  "rounded-full bg-[#A86946] text-white text-[10px] uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-[#1A1B18] transition-colors duration-300 disabled:opacity-40 inline-flex items-center gap-1.5";
const btnOutline =
  "rounded-full border border-[#A86946] text-[#A86946] text-[10px] uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-[#A86946] hover:text-white transition-colors duration-300 disabled:opacity-40 inline-flex items-center gap-1.5";
const btnGhost =
  "rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300 disabled:opacity-40 inline-flex items-center gap-1.5";

const busyLabel = (busy) =>
  busy === "retouch" || busy === "generate" ? "L'AI sta lavorando… (fino a un minuto)" : "Attendere…";

const ImageSlot = ({ slot, config, reload }) => {
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const custom = config[slot.key];
  const src = custom ? `${API}/files/${custom}` : slot.def;

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione non riuscita. Riprova.");
    } finally {
      setBusy("");
    }
  };

  const currentAsFile = async () => {
    if (file) return file;
    if (!custom) return null;
    const blob = await fetch(src).then((r) => r.blob());
    return new File([blob], "attuale.webp", { type: blob.type || "image/webp" });
  };

  const upload = () => {
    if (!file) return toast.error("Seleziona prima una foto.");
    run("upload", async () => {
      const fd = new FormData();
      fd.append("key", slot.key);
      fd.append("file", file);
      await axios.post(`${API}/admin/site-image`, fd, { withCredentials: true });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Immagine aggiornata!");
      reload();
    });
  };

  const retouch = () =>
    run("retouch", async () => {
      const f = await currentAsFile();
      if (!f) {
        toast.error("Seleziona una foto da ritoccare (o carica prima un'immagine personalizzata).");
        return;
      }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("save_as", slot.key);
      await axios.post(`${API}/admin/ai-retouch`, fd, { withCredentials: true, timeout: 150000 });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Ritocco completato e pubblicato!");
      reload();
    });

  const generate = () => {
    if (!prompt.trim()) return toast.error("Descrivi l'immagine da generare.");
    run("generate", async () => {
      await axios.post(
        `${API}/admin/ai-generate`,
        { prompt: prompt.trim(), save_as: slot.key },
        { withCredentials: true, timeout: 150000 }
      );
      setPrompt("");
      toast.success("Immagine generata e pubblicata!");
      reload();
    });
  };

  const reset = () =>
    run("reset", async () => {
      await axios.delete(`${API}/admin/site-image/${slot.key}`, { withCredentials: true });
      toast.success("Ripristinata l'immagine di default.");
      reload();
    });

  return (
    <div data-testid={`slot-${slot.key}`} className="border border-[#DCD7CC] bg-white/50 p-6">
      <h3 className="font-display text-lg font-medium mb-1">{slot.label}</h3>
      <p className="text-xs text-[#1A1B18]/50 mb-5">{slot.desc}</p>
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <div className={`${slot.imgCls} border border-[#DCD7CC] bg-[#FAF9F6] overflow-hidden shrink-0`}>
          <img src={src} alt={slot.label} data-testid={`slot-preview-${slot.key}`} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 space-y-4 min-w-0">
          <input
            type="file"
            accept="image/*"
            ref={fileRef}
            onChange={(e) => setFile(e.target.files[0] || null)}
            data-testid={`slot-file-${slot.key}`}
            className="text-sm w-full"
          />
          <div className="flex flex-wrap gap-2">
            <button onClick={upload} disabled={!!busy} data-testid={`slot-upload-${slot.key}`} className={btnPrimary}>
              <ImagePlus size={13} /> Carica
            </button>
            <button onClick={retouch} disabled={!!busy} data-testid={`slot-retouch-${slot.key}`} className={btnOutline}>
              <Sparkles size={13} /> Ritocca con AI
            </button>
            {custom && (
              <button onClick={reset} disabled={!!busy} data-testid={`slot-reset-${slot.key}`} className={btnGhost}>
                <RotateCcw size={13} /> Ripristina
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Genera con AI: descrivi l'immagine…"
              data-testid={`slot-prompt-${slot.key}`}
              className="flex-1 min-w-0 rounded-xl border border-[#DCD7CC] bg-transparent h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]"
            />
            <button onClick={generate} disabled={!!busy} data-testid={`slot-generate-${slot.key}`} className={btnOutline}>
              <Wand2 size={13} /> Genera
            </button>
          </div>
          {busy && <p className="text-xs text-[#A86946] animate-pulse">{busyLabel(busy)}</p>}
          {!custom && <p className="text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/40">Immagine di default del sito</p>}
        </div>
      </div>
    </div>
  );
};

const PhotoList = ({ listKey, title, desc, defaults, resetLabel, config, reload }) => {
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const custom = config[listKey];
  const shown = custom?.length
    ? custom.map((p) => (p.startsWith("/") ? p : `${API}/files/${p}`))
    : defaults;
  const atCap = shown.length >= 12;

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione non riuscita. Riprova.");
    } finally {
      setBusy("");
    }
  };

  const addByPath = (path) =>
    run("add", async () => {
      const fd = new FormData();
      fd.append("path", path);
      fd.append("list_key", listKey);
      await axios.post(`${API}/admin/hero-slides`, fd, { withCredentials: true });
      toast.success("Foto aggiunta!");
      reload();
    });

  const upload = () => {
    if (!file) return toast.error("Seleziona prima una foto.");
    run("upload", async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("list_key", listKey);
      await axios.post(`${API}/admin/hero-slides`, fd, { withCredentials: true });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Foto aggiunta!");
      reload();
    });
  };

  const retouch = () => {
    if (!file) return toast.error("Seleziona una foto da ritoccare.");
    run("retouch", async () => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/ai-retouch`, fd, { withCredentials: true, timeout: 150000 });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await addByPath(r.data.path);
    });
  };

  const generate = () => {
    if (!prompt.trim()) return toast.error("Descrivi l'immagine da generare.");
    run("generate", async () => {
      const r = await axios.post(
        `${API}/admin/ai-generate`,
        { prompt: prompt.trim() },
        { withCredentials: true, timeout: 150000 }
      );
      setPrompt("");
      await addByPath(r.data.path);
    });
  };

  const remove = (i) =>
    run("remove", async () => {
      await axios.delete(`${API}/admin/hero-slides/${i}?list_key=${listKey}`, { withCredentials: true });
      toast.success("Foto rimossa.");
      reload();
    });

  const resetAll = () =>
    run("reset", async () => {
      await axios.delete(`${API}/admin/site-image/${listKey}`, { withCredentials: true });
      toast.success("Lista ripristinata ai contenuti originali.");
      reload();
    });

  return (
    <div data-testid={`list-${listKey}`} className="border border-[#DCD7CC] bg-white/50 p-6">
      <h3 className="font-display text-lg font-medium mb-1">{title}</h3>
      <p className="text-xs text-[#1A1B18]/50 mb-5">{desc}</p>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-5">
        {shown.map((s, i) => (
          <div key={`${s}-${i}`} data-testid={`${listKey}-thumb-${i}`} className="relative group aspect-[16/10] border border-[#DCD7CC] overflow-hidden bg-[#FAF9F6]">
            <img src={s} alt={`Foto ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
            {custom && (
              <button
                onClick={() => remove(i)}
                data-testid={`${listKey}-remove-${i}`}
                aria-label={`Rimuovi foto ${i + 1}`}
                className="absolute top-1 right-1 bg-[#1A1B18]/80 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
      {!custom && (
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/40 mb-4">
          Contenuto di default. Aggiungi una tua foto per iniziare a personalizzarlo.
        </p>
      )}
      <div className="space-y-4">
        <input
          type="file"
          accept="image/*"
          ref={fileRef}
          onChange={(e) => setFile(e.target.files[0] || null)}
          data-testid={`${listKey}-file-input`}
          className="text-sm w-full"
        />
        <div className="flex flex-wrap gap-2">
          <button onClick={upload} disabled={!!busy || atCap} data-testid={`${listKey}-upload`} className={btnPrimary}>
            <Plus size={13} /> Aggiungi foto
          </button>
          <button onClick={retouch} disabled={!!busy || atCap} data-testid={`${listKey}-retouch`} className={btnOutline}>
            <Sparkles size={13} /> Ritocca e aggiungi
          </button>
          {custom && (
            <button onClick={resetAll} disabled={!!busy} data-testid={`${listKey}-reset`} className={btnGhost}>
              <RotateCcw size={13} /> {resetLabel}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Genera con AI: descrivi la foto…"
            data-testid={`${listKey}-prompt`}
            className="flex-1 min-w-0 rounded-xl border border-[#DCD7CC] bg-transparent h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]"
          />
          <button onClick={generate} disabled={!!busy || atCap} data-testid={`${listKey}-generate`} className={btnOutline}>
            <Wand2 size={13} /> Genera e aggiungi
          </button>
        </div>
        {atCap && <p className="text-xs text-[#A86946]">Hai raggiunto il massimo di 12 foto: eliminane una per aggiungerne un'altra.</p>}
        {busy && <p className="text-xs text-[#A86946] animate-pulse">{busyLabel(busy)}</p>}
      </div>
    </div>
  );
};

export default function ContenutiPanel() {
  const [config, setConfig] = useState(null);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/site-config`);
      setConfig(r.data);
    } catch {
      toast.error("Errore nel caricamento della configurazione.");
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (config === null) {
    return <p data-testid="contenuti-loading" className="text-sm text-[#1A1B18]/50">Caricamento…</p>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <PhotoList
        listKey="hero_slides"
        title="Slideshow iniziale"
        desc="Le foto a tutta larghezza in cima alla pagina. Puoi sostituirle, aggiungerne (fino a 12) o eliminarle. Consigliate foto orizzontali."
        defaults={DEFAULT_SLIDES}
        resetLabel="Ripristina le 6 originali"
        config={config}
        reload={reload}
      />
      <PhotoList
        listKey="store_photos"
        title='Foto del negozio — "Shopping in negozio"'
        desc="Le foto reali del locale mostrate nella grande card in fondo alla sezione Servizi (cambiano da sole con dissolvenza)."
        defaults={DEFAULT_STORE}
        resetLabel="Ripristina foto di default"
        config={config}
        reload={reload}
      />
      {SLOTS.map((s) => (
        <ImageSlot key={s.key} slot={s} config={config} reload={reload} />
      ))}
    </div>
  );
}
